import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdArrowBack, MdBlock, MdCheckCircle, MdEdit, MdNotes, MdShield,
  MdWarningAmber, MdDeleteForever, MdRestore, MdClose, MdArrowForward } from 'react-icons/md';
import { FaUserSecret, FaTag, FaTrash, FaPlus, FaStickyNote } from 'react-icons/fa';
import toast from 'react-hot-toast';
import saApi from '../../services/superAdminApi';
import { getPlans } from '../../services/subscriptionService';
import { formatAdBs } from '../../utils/calendar';
import { getStatusBadge } from '../../utils/clubStatus';

// Status pill colors for the subscription summary card (dark theme)
// Platform payment status pill (dark theme)
const PAYMENT_PILL = {
  pending:  { label: 'Pending',  bg: 'rgba(120,53,15,0.5)',  color: '#fcd34d' },
  verified: { label: 'Verified', bg: 'rgba(20,83,45,0.5)',   color: '#4ade80' },
  rejected: { label: 'Rejected', bg: 'rgba(127,29,29,0.5)',  color: '#fca5a5' },
};
const fmtShort = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const SUB_STATUS_STYLE = {
  Active:            { bg: 'rgba(34,197,94,0.15)',  color: '#4ade80' },
  Trial:             { bg: 'rgba(96,165,250,0.15)',  color: '#60a5fa' },
  'Past Due':        { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
  'Payment overdue': { bg: 'rgba(239,68,68,0.15)',   color: '#f87171' },
  Suspended:         { bg: 'rgba(239,68,68,0.15)',   color: '#f87171' },
  'Trial Expired':   { bg: 'rgba(239,68,68,0.15)',   color: '#f87171' },
  Cancelled:         { bg: 'rgba(107,101,96,0.2)',  color: '#8a847c' },
  Archived:          { bg: 'rgba(107,101,96,0.2)',  color: '#8a847c' },
};

// Header status badge uses the shared getStatusBadge() map (imported above).

const RISK_LEVEL_STYLE = {
  high:   { bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.3)',   text: '#f87171', label: 'HIGH'   },
  medium: { bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.3)',  text: '#fbbf24', label: 'MEDIUM' },
  low:    { bg: 'rgba(107,114,128,0.08)', border: 'rgba(107,114,128,0.3)', text: '#8a847c', label: 'LOW'    },
};

const SEV_STYLE = {
  high:   { bg: 'rgba(239,68,68,0.12)',   text: '#f87171', border: 'rgba(239,68,68,0.3)'   },
  medium: { bg: 'rgba(251,191,36,0.10)',  text: '#fbbf24', border: 'rgba(251,191,36,0.3)'  },
  low:    { bg: 'rgba(107,114,128,0.12)', text: '#8a847c', border: 'rgba(107,114,128,0.3)' },
};

function computeDetailRisks(club, studentCount, activeDiscount) {
  const risks = [];
  const now = new Date();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  if (club.subscriptionPlan === 'pro' && club.status === 'suspended')
    risks.push({ severity: 'high', label: 'Suspended Pro Club', action: 'Reactivate club or resolve payment issue with owner.' });
  if (club.subscriptionPlan === 'free' && studentCount > (club.maxMembers || 15))
    risks.push({ severity: 'high', label: 'Over Member Limit', action: `Free plan allows ${club.maxMembers || 15} members but club has ${studentCount} students. Upgrade to Pro.` });
  if (club.status === 'trial_expired')
    risks.push({ severity: 'high', label: 'Trial Expired', action: 'Trial ended without a plan upgrade. Follow up to convert.' });
  if (!club.ownerEmail)
    risks.push({ severity: 'high', label: 'Missing Owner Email', action: 'No owner email on record — billing and critical notices cannot be sent.' });

  const isTrial = club.status === 'trial' || club.status === 'trialing';
  if (isTrial && club.trialEndsAt) {
    const days = Math.ceil((new Date(club.trialEndsAt) - now) / 86400000);
    if (days > 0 && days <= 3)
      risks.push({ severity: 'high', label: `Trial Expiring in ${days}d`, action: 'Contact owner immediately to convert to a paid plan.' });
    else if (days > 3 && days <= 7)
      risks.push({ severity: 'medium', label: `Trial Expiring in ${days}d`, action: 'Reach out about paid plan options before trial ends.' });
  }

  if (activeDiscount) {
    const isStale = activeDiscount.isActive && activeDiscount.expiresAt && new Date(activeDiscount.expiresAt) < now;
    if (isStale) {
      risks.push({ severity: 'medium', label: 'Stale Discount', action: 'Discount expired but still marked active. Run discount cleanup to fix MRR.' });
    } else if (activeDiscount.expiresAt) {
      const discDays = Math.ceil((new Date(activeDiscount.expiresAt) - now) / 86400000);
      if (discDays > 0 && discDays <= 7)
        risks.push({ severity: 'medium', label: `Discount Expiring in ${discDays}d`, action: 'Decide whether to renew or let the discount expire.' });
    }
  }

  const clubAge = now - new Date(club.createdAt);
  if (clubAge > sevenDaysMs && studentCount === 0 && club.status !== 'archived' && club.status !== 'cancelled')
    risks.push({ severity: 'low', label: 'No Students', action: 'Club has been active over a week with no students. Offer onboarding support.' });

  const ORDER = { high: 3, medium: 2, low: 1 };
  const topLevel = risks.reduce((max, r) => (ORDER[r.severity] || 0) > (ORDER[max] || 0) ? r.severity : max, null);
  return { risks, riskLevel: topLevel };
}

const NOTE_TYPE_COLORS = {
  support:   { bg: '#1e3a5f', color: '#60a5fa' },
  billing:   { bg: '#14532d', color: '#4ade80' },
  discount:  { bg: '#3b1764', color: '#c084fc' },
  sales:     { bg: '#78350f', color: '#fcd34d' },
  technical: { bg: '#332f28', color: '#8a847c' },
  other:     { bg: '#28261f', color: '#6b6560' },
};

function ImpersonateModal({ club, onClose }) {
  const [reason, setReason] = useState('');
  const [mode,   setMode]   = useState('readonly');
  const [loading, setLoading] = useState(false);
  useEscapeKey(onClose, !loading);

  const handleStart = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (reason.trim().length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    setLoading(true);
    try {
      const { data } = await saApi.post('/api/super-admin/impersonation/start', {
        clubId: club._id, reason, mode,
      });
      localStorage.setItem('trainaxis_imp_session', JSON.stringify({
        sessionId:   data.session.id,
        clubName:    data.club.name,
        clubSlug:    data.club.slug,
        expiresAt:   data.session.expiresAt,
        mode:        data.session.mode,
        reason:      reason.trim(),
      }));
      localStorage.setItem('trainaxis_token',     data.token);
      localStorage.setItem('trainaxis_user',      JSON.stringify(data.adminUser));
      localStorage.setItem('trainaxis_club_slug', data.club.slug);
      toast.success(`Impersonation started — ${data.club.name}`);
      window.location.href = '/admin/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start impersonation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !loading && onClose()}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#7f1d1d' }}>
            <FaUserSecret className="text-red-400 text-lg" />
          </div>
          <div>
            <h2 className="font-bold text-[#ede8e0]">Access Club as Admin</h2>
            <p className="text-xs" style={{ color: '#8a847c' }}>{club.name} · All actions will be logged</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Reason for access *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="e.g. Customer reported missing data, investigating issue #123"
            className="w-full border rounded-xl px-4 py-3 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] resize-none"
            style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
          {reason.trim().length > 0 && reason.trim().length < 10 && (
            <p className="text-xs mt-1.5" style={{ color: '#8a847c' }}>
              {10 - reason.trim().length} more character{10 - reason.trim().length === 1 ? '' : 's'} needed
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#c5bfb5] mb-2">Access Mode</label>
          <div className="flex gap-2">
            {[
              { value: 'readonly',    label: 'Read-Only',   desc: 'Cannot make changes' },
              { value: 'full_access', label: 'Full Access', desc: 'Can modify data'      },
            ].map(m => (
              <button key={m.value} onClick={() => setMode(m.value)}
                className="flex-1 p-3 rounded-xl border-2 text-left transition-all"
                style={{ background: '#1a1918', borderColor: mode === m.value ? '#c8102e' : 'rgba(237,232,224,0.08)' }}>
                <div className="text-sm font-semibold text-[#ede8e0]">{m.label}</div>
                <div className="text-xs mt-0.5" style={{ color: '#6b6560' }}>{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {mode === 'full_access' && (
          <div className="rounded-xl p-3 text-sm" style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #991b1b' }}>
            Full access mode. You can modify this club's data. This is logged and visible to the club owner.
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ background: '#28261f', color: '#c5bfb5' }}>
            Cancel
          </button>
          <button onClick={handleStart} disabled={loading || reason.trim().length < 10}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#c8102e' }}>
            {loading ? 'Starting…' : 'Enter Club'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditSubscriptionModal({ club, onClose, onSaved }) {
  const [plan,    setPlan]    = useState(club.subscriptionPlan || 'free');
  // Read-only here — the billing modal never changes lifecycle status (use Reactivate/Suspend).
  const [status]  = useState(club.status || 'active');
  const [maxMembers, setMaxMembers] = useState(club.maxMembers || 15);
  const [loading, setLoading] = useState(false);
  useEscapeKey(onClose, !loading);

  // Single source of truth for the Pro price is backend/config/plans.js — fetched here rather
  // than hardcoded, so this label can never drift from it.
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const proPrice = plansData?.pro?.price;

  const handleSave = async () => {
    setLoading(true);
    try {
      // Billing-only: send plan + maxMembers, NOT status (lifecycle is changed via Reactivate/Suspend).
      await saApi.put(`/api/super-admin/clubs/${club._id}/subscription`, { plan, maxMembers: Number(maxMembers) });
      toast.success('Subscription updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 10, padding: '8px 12px', color: '#ede8e0', fontSize: 13, outline: 'none', width: '100%' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8a847c', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !loading && onClose()}>
      <div className="w-full max-w-sm rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-[#ede8e0] flex items-center gap-2"><MdEdit /> Edit Subscription</h2>
        <div>
          <label style={labelStyle}>Plan</label>
          <select style={inputStyle} value={plan} onChange={e => setPlan(e.target.value)}>
            <option value="free">Free (15 members, NPR 0)</option>
            <option value="pro">Pro (Unlimited, NPR {proPrice != null ? proPrice.toLocaleString() : '…'}/mo)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} value={status} disabled>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="trialing">Trialing</option>
            <option value="trial_expired">Trial Expired</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <p className="text-xs mt-1" style={{ color: '#6b6560' }}>Use the Reactivate / Suspend buttons to change club status.</p>
        </div>
        <div>
          <label style={labelStyle}>Max Members</label>
          <input style={inputStyle} type="number" min="1" value={maxMembers} onChange={e => setMaxMembers(e.target.value)} />
          {plan === 'pro' && <p className="text-xs mt-1" style={{ color: '#6b6560' }}>Pro plan: typically 9999 (unlimited)</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors" style={{ borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors" style={{ background: loading ? '#332f28' : '#c8102e' }}>
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Suspend modal — matches the Edit Subscription modal style; reason ≥ 10 chars (same rule as impersonation).
function SuspendModal({ club, onClose, onConfirm, pending }) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 10;
  useEscapeKey(onClose, !pending);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !pending && onClose()}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#7f1d1d' }}>
            <MdBlock className="text-red-400 text-lg" />
          </div>
          <div>
            <h2 className="font-bold text-[#ede8e0]">Suspend Club</h2>
            <p className="text-xs" style={{ color: '#8a847c' }}>{club.name} · This blocks all club access</p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Reason for suspension *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="e.g. Non-payment after grace period; owner unresponsive to reminders"
            className="w-full border rounded-xl px-4 py-3 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] resize-none"
            style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
          {reason.trim().length > 0 && reason.trim().length < 10 && (
            <p className="text-xs mt-1.5" style={{ color: '#8a847c' }}>
              {10 - reason.trim().length} more character{10 - reason.trim().length === 1 ? '' : 's'} needed
            </p>
          )}
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors"
            style={{ borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
            Cancel
          </button>
          <button onClick={() => valid && onConfirm(reason.trim())} disabled={!valid || pending}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#c8102e' }}>
            {pending ? 'Suspending…' : 'Suspend Club'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Generic confirm modal (replaces window.confirm) — tone controls the action button colour.
function ConfirmModal({ title, message, confirmLabel, tone = 'danger', onCancel, onConfirm }) {
  const btnBg = tone === 'success' ? '#059669' : tone === 'warn' ? '#d97706' : '#c8102e';
  useEscapeKey(onCancel);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-[#ede8e0]">{title}</h2>
        <p className="text-sm leading-relaxed" style={{ color: '#c5bfb5' }}>{message}</p>
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onCancel}
            className="text-sm font-semibold px-4 py-2 rounded-lg"
            style={{ background: '#28261f', color: '#c5bfb5' }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-[#ede8e0]"
            style={{ background: btnBg }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// KTM date label for the countdown / scheduled banner.
const KTM_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;
const fmtKtmDate = (d) => {
  if (!d) return '';
  const k = new Date(new Date(d).getTime() + KTM_OFFSET_MS);
  return k.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const daysUntil = (d) => Math.max(0, Math.ceil((new Date(d).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));

// ── Slug type-to-confirm input (paste blocked, exact match) ──────────────────────
function SlugConfirm({ slug, value, onChange }) {
  return (
    <div>
      <p className="text-sm mb-2" style={{ color: '#c5bfb5' }}>
        Type <span className="font-mono font-bold" style={{ color: '#ede8e0' }}>{slug}</span> to continue
      </p>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onPaste={e => e.preventDefault()}
        autoComplete="off" autoCorrect="off" spellCheck={false}
        placeholder={slug}
        className="w-full rounded-xl px-4 py-3 text-sm font-mono outline-none"
        style={{ background: '#141312', border: `1px solid ${value && value !== slug ? 'rgba(239,68,68,0.6)' : 'rgba(237,232,224,0.10)'}`, color: '#ede8e0' }}
      />
    </div>
  );
}

// ── Delete Club — three steps in one flow ────────────────────────────────────────
function DeleteClubModal({ club, onClose, onScheduled }) {
  useEscapeKey(onClose);
  const [step, setStep]     = useState(1);
  const [reason, setReason] = useState('');
  const [slugText, setSlugText] = useState('');
  const [scheduledAt, setScheduledAt] = useState(null);

  const { data: preview, isPending: previewLoading } = useQuery({
    queryKey: ['sa-club-deletion-preview', club._id],
    queryFn:  () => saApi.get(`/api/super-admin/clubs/${club._id}/deletion-preview`).then(r => r.data),
  });
  const counts  = preview?.counts || {};
  const blocked = preview?.blockedBySubscription;
  const grace   = preview?.graceDays || 7;

  const mutation = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${club._id}/delete`, { reason: reason.trim(), confirmSlug: slugText.trim() }),
    onSuccess:  (r) => { setScheduledAt(r.data?.club?.deletionScheduledAt); setStep(3); onScheduled?.(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Delete failed'),
  });

  const consequence = [
    ['students', 'students'], ['trainers', 'trainers'], ['classes', 'classes'],
    ['payments', 'fee payments'], ['sessions', 'sessions'], ['beltExams', 'belt exams'],
  ].filter(([k]) => (counts[k] || 0) > 0);

  const overlay = { position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const card    = { width: '100%', maxWidth: 480, background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.10)', borderRadius: 16, maxHeight: '92vh', display: 'flex', flexDirection: 'column' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={card}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
          <h2 className="font-bold flex items-center gap-2" style={{ color: '#ede8e0' }}>
            <MdDeleteForever style={{ color: '#f87171' }} /> Delete {club.name}
          </h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ color: '#8a847c' }}><MdClose /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto" style={{ flex: 1 }}>
          {/* Step 1 — consequences + grace + reason */}
          {step === 1 && (
            <div className="space-y-4">
              {blocked && (
                <div className="rounded-xl p-3 text-sm flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171' }}>
                  <MdWarningAmber className="flex-shrink-0 mt-0.5" />
                  <span>This club has an active paid subscription. Suspend or cancel its billing before deleting.</span>
                </div>
              )}
              <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#f87171' }}>This will permanently delete</p>
                {previewLoading ? (
                  <p className="text-sm" style={{ color: '#8a847c' }}>Loading counts…</p>
                ) : consequence.length ? (
                  <ul className="text-sm space-y-1" style={{ color: '#c5bfb5' }}>
                    {consequence.map(([k, label]) => (
                      <li key={k}>• <span className="font-bold" style={{ color: '#ede8e0' }}>{counts[k].toLocaleString()}</span> {label}</li>
                    ))}
                    <li className="pt-1" style={{ color: '#8a847c' }}>…and all settings, notifications, records and uploaded files.</li>
                  </ul>
                ) : (
                  <p className="text-sm" style={{ color: '#8a847c' }}>All tenant data for this club.</p>
                )}
              </div>
              <div className="rounded-xl p-3 text-xs" style={{ background: '#141312', color: '#8a847c', border: '1px solid rgba(237,232,224,0.08)' }}>
                <MdRestore className="inline mr-1" style={{ color: '#34d399' }} />
                Recoverable for <strong style={{ color: '#c5bfb5' }}>{grace} days</strong>. The club is made inaccessible immediately; permanent purge runs after the grace window (or manually).
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#ede8e0' }}>Reason <span style={{ color: '#f87171' }}>*</span></label>
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
                  placeholder="Why is this club being deleted? (min 10 characters, stored)"
                  className="w-full rounded-xl px-3 py-2.5 text-sm outline-none resize-none"
                  style={{ background: '#141312', border: '1px solid rgba(237,232,224,0.10)', color: '#ede8e0' }} />
              </div>
            </div>
          )}

          {/* Step 2 — type-to-confirm */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: '#c5bfb5' }}>Final safety check. This schedules permanent deletion.</p>
              <SlugConfirm slug={club.slug} value={slugText} onChange={setSlugText} />
            </div>
          )}

          {/* Step 3 — scheduled */}
          {step === 3 && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.12)' }}>
                <MdDeleteForever className="text-3xl" style={{ color: '#f87171' }} />
              </div>
              <h3 className="font-bold text-lg mb-2" style={{ color: '#ede8e0' }}>Deletion scheduled for {fmtKtmDate(scheduledAt)}</h3>
              <p className="text-sm" style={{ color: '#8a847c' }}>The club is now inaccessible. You can restore it any time until then.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3" style={{ borderTop: '1px solid rgba(237,232,224,0.08)' }}>
          {step === 1 && (
            <>
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium" style={{ color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)' }}>Cancel</button>
              <button disabled={blocked || reason.trim().length < 10} onClick={() => setStep(2)}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-1.5"
                style={{ background: blocked || reason.trim().length < 10 ? '#332f28' : '#c8102e', color: '#fff', opacity: blocked || reason.trim().length < 10 ? 0.5 : 1 }}>
                Continue <MdArrowForward />
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-xl text-sm font-medium" style={{ color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)' }}>Back</button>
              <button disabled={slugText.trim() !== club.slug || mutation.isPending} onClick={() => mutation.mutate()}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: slugText.trim() !== club.slug ? '#332f28' : '#c8102e', color: '#fff', opacity: slugText.trim() !== club.slug ? 0.5 : 1 }}>
                {mutation.isPending ? 'Scheduling…' : 'Schedule deletion'}
              </button>
            </>
          )}
          {step === 3 && (
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#c8102e', color: '#fff' }}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Purge now — re-requires slug typing ──────────────────────────────────────────
function PurgeModal({ club, onClose, onPurged }) {
  useEscapeKey(onClose);
  const [slugText, setSlugText] = useState('');
  const mutation = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${club._id}/purge`, { confirmSlug: slugText.trim() }),
    onSuccess:  () => { toast.success('Club permanently purged'); onPurged?.(); onClose(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Purge failed'),
  });
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: '100%', maxWidth: 440, background: '#1e1d1b', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 16 }}>
        <div className="px-5 py-4" style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
          <h2 className="font-bold flex items-center gap-2" style={{ color: '#ede8e0' }}><MdDeleteForever style={{ color: '#f87171' }} /> Purge now</h2>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm" style={{ color: '#f87171' }}>
            This <strong>permanently destroys all data</strong> for {club.name} immediately — no grace window, no recovery.
          </p>
          <SlugConfirm slug={club.slug} value={slugText} onChange={setSlugText} />
        </div>
        <div className="flex justify-end gap-2 px-5 py-3" style={{ borderTop: '1px solid rgba(237,232,224,0.08)' }}>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium" style={{ color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)' }}>Cancel</button>
          <button disabled={slugText.trim() !== club.slug || mutation.isPending} onClick={() => mutation.mutate()}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: slugText.trim() !== club.slug ? '#332f28' : '#c8102e', color: '#fff', opacity: slugText.trim() !== club.slug ? 0.5 : 1 }}>
            {mutation.isPending ? 'Purging…' : 'Purge permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClubDetail() {
  const { id }      = useParams();
  const [showImp,  setShowImp]  = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showPurge, setShowPurge] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, tone, onConfirm }
  const [transferTarget, setTransferTarget] = useState(null); // admin to transfer Primary to
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('support');
  const [proofView, setProofView] = useState(null); // { url } for image preview
  const qc = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['sa-club', id],
    queryFn:  () => saApi.get(`/api/super-admin/clubs/${id}`).then(r => r.data),
  });

  // Platform payment history for THIS club (newest first, from the backend).
  const { data: clubPayData } = useQuery({
    queryKey: ['sa-club-platform-payments', id],
    queryFn:  () => saApi.get('/api/subscription/admin/platform-payments', { params: { status: 'all', clubId: id } }).then(r => r.data),
    enabled:  !!id,
  });
  const clubPayments = clubPayData?.payments || [];

  // Image proofs open in a preview modal; PDFs open in a new tab.
  const viewProof = (p) => {
    if (!p.proofUrl) return;
    const isPdf = (p.proofMime || '').includes('pdf') || /\.pdf($|\?)/i.test(p.proofUrl);
    if (isPdf) window.open(p.proofUrl, '_blank', 'noopener,noreferrer');
    else setProofView({ url: p.proofUrl });
  };

  const { data: discountData } = useQuery({
    queryKey: ['sa-club-discount', id],
    queryFn:  () => saApi.get('/api/super-admin/discounts/applied', { params: { clubId: id } }).then(r => r.data),
    enabled:  !!id,
  });
  const activeDiscount = (discountData?.appliedDiscounts || []).find(a => a.isActive && (a.clubId?._id === id || a.clubId === id));

  const { data: notesData } = useQuery({
    queryKey: ['sa-club-notes', id],
    queryFn:  () => saApi.get(`/api/super-admin/clubs/${id}/notes`).then(r => r.data),
    enabled:  !!id,
  });
  const notes = notesData?.notes || [];

  // Read-only admin roster (Primary/Staff visibility — no actions yet).
  const { data: adminData } = useQuery({
    queryKey: ['sa-club-admins', id],
    queryFn:  () => saApi.get(`/api/super-admin/clubs/${id}/admins`).then(r => r.data),
    enabled:  !!id,
  });
  const clubAdmins = adminData?.admins || [];

  const suspendMutation = useMutation({
    mutationFn: (reason) => saApi.put(`/api/super-admin/clubs/${id}/suspend`, { reason }),
    onSuccess:  () => { toast.success('Club suspended'); qc.invalidateQueries({ queryKey: ['sa-club', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Error'),
  });
  const reactivateMutation = useMutation({
    mutationFn: () => saApi.put(`/api/super-admin/clubs/${id}/reactivate`),
    onSuccess:  () => { toast.success('Club reactivated'); qc.invalidateQueries({ queryKey: ['sa-club', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Error'),
  });
  const restoreMutation = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${id}/restore`),
    onSuccess:  () => { toast.success('Club restored'); qc.invalidateQueries({ queryKey: ['sa-club', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Restore failed'),
  });
  const addNoteMutation = useMutation({
    mutationFn: ({ note, type }) => saApi.post(`/api/super-admin/clubs/${id}/notes`, { note, type }),
    onSuccess:  () => {
      setNoteText('');
      toast.success('Note added');
      qc.invalidateQueries({ queryKey: ['sa-club-notes', id] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to add note'),
  });
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) => saApi.delete(`/api/super-admin/clubs/${id}/notes/${noteId}`),
    onSuccess:  () => { toast.success('Note deleted'); qc.invalidateQueries({ queryKey: ['sa-club-notes', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to delete note'),
  });
  const reactivateAdminMutation = useMutation({
    mutationFn: (userId) => saApi.post(`/api/super-admin/clubs/${id}/admins/${userId}/reactivate`),
    onSuccess:  () => { toast.success('Admin reactivated'); qc.invalidateQueries({ queryKey: ['sa-club-admins', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to reactivate admin'),
  });
  const setPrimaryMutation = useMutation({
    mutationFn: (userId) => saApi.post(`/api/super-admin/clubs/${id}/set-primary-admin`, { userId }),
    onSuccess:  () => {
      toast.success('Primary Admin transferred successfully.');
      setTransferTarget(null);
      qc.invalidateQueries({ queryKey: ['sa-club-admins', id] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to transfer Primary Admin'),
  });

  // Escape closes the inline modals (both already close on backdrop click). Ignored mid-transfer.
  useEscapeKey(() => setProofView(null), !!proofView);
  useEscapeKey(() => setTransferTarget(null), !!transferTarget && !setPrimaryMutation.isPending);

  if (isPending) return <div style={{ color: '#6b6560' }}>Loading…</div>;
  if (!data)     return <div style={{ color: '#ef4444' }}>Club not found</div>;

  const { club, settings, recentAccess } = data;
  const sub = data.subscriptionSummary || {};
  const pendingPay = data.pendingPlatformPayment || null;
  const statusBadge = getStatusBadge(club.status);
  const { risks, riskLevel } = computeDetailRisks(
    club, data.club.memberCounts?.students || 0, activeDiscount
  );

  const inputStyle = { background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 10, padding: '8px 12px', color: '#ede8e0', fontSize: 13, outline: 'none' };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Link to="/superadmin/clubs" className="text-sm hover:underline" style={{ color: '#c8102e' }}>
          <MdArrowBack className="inline mr-1" />Clubs
        </Link>
      </div>

      {/* Pending-deletion banner — countdown, reason, who, restore/purge */}
      {club.status === 'pending_deletion' && (
        <div className="rounded-2xl p-5" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="font-bold flex items-center gap-2" style={{ color: '#f87171' }}>
                <MdDeleteForever /> Scheduled for permanent deletion
              </p>
              <p className="text-sm mt-1" style={{ color: '#c5bfb5' }}>
                Purges in <strong style={{ color: '#ede8e0' }}>{daysUntil(club.deletionScheduledAt)} day{daysUntil(club.deletionScheduledAt) === 1 ? '' : 's'}</strong> — on {fmtKtmDate(club.deletionScheduledAt)}. The club is inaccessible until then.
              </p>
              {club.deletionReason && <p className="text-xs mt-2" style={{ color: '#8a847c' }}>Reason: “{club.deletionReason}”</p>}
              {club.deletedByName && <p className="text-xs" style={{ color: '#8a847c' }}>Scheduled by {club.deletedByName}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => restoreMutation.mutate()} disabled={restoreMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold"
                style={{ background: '#1e5f43', color: '#d1fae5' }}>
                <MdRestore /> {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
              </button>
              <button onClick={() => setShowPurge(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold"
                style={{ background: '#c8102e', color: '#fff' }}>
                <MdDeleteForever /> Purge now…
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Risk panel — shown only when issues exist */}
      {risks.length > 0 && (() => {
        const ls = RISK_LEVEL_STYLE[riskLevel] || RISK_LEVEL_STYLE.low;
        return (
          <div className="rounded-xl p-4 border" style={{ background: ls.bg, borderColor: ls.border }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FaStickyNote style={{ color: ls.text, fontSize: 14 }} />
                <span className="text-sm font-bold" style={{ color: ls.text }}>
                  Risk Alert — {risks.length} issue{risks.length !== 1 ? 's' : ''} detected
                </span>
                <span className="text-xs font-bold px-2 py-0.5 rounded"
                  style={{ background: ls.border, color: ls.text }}>
                  {ls.label}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {risks.map((r, i) => {
                const ss = SEV_STYLE[r.severity] || SEV_STYLE.low;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-lg px-3 py-2.5"
                    style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${ss.border}` }}>
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5"
                      style={{ background: ss.bg, color: ss.text, border: `1px solid ${ss.border}` }}>
                      {r.severity.toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: '#ede8e0' }}>{r.label}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#8a847c' }}>{r.action}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="rounded-xl p-6 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <div className="flex flex-wrap gap-4 justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#ede8e0]">{club.name}</h1>
            <p className="text-sm mt-1" style={{ color: '#8a847c' }}>{club.slug}.trainaxis.com · {club.clubType}</p>
            <div className="flex gap-2 mt-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={statusBadge.style}>{statusBadge.label}</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa' }}>
                {club.subscriptionPlan}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Primary action */}
            <button onClick={() => setShowImp(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white"
              style={{ background: '#c8102e' }}>
              <FaUserSecret /> Login as Admin
            </button>
            {/* Secondary / outline actions — consistent sizing + weight */}
            <button onClick={() => setShowEdit(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors"
              style={{ background: 'transparent', borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
              <MdEdit /> Edit Subscription
            </button>
            {/* Suspend / Reactivate moved to the Danger Zone at the bottom of the page. */}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Students', value: club.memberCounts?.students },
          { label: 'Trainers', value: club.memberCounts?.trainers },
          { label: 'Admins',   value: club.memberCounts?.admins   },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl p-4 text-center border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
            <div className="text-2xl font-bold text-[#ede8e0]">{value ?? 0}</div>
            <div className="text-xs mt-1" style={{ color: '#8a847c' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Club info */}
      <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <h3 className="font-semibold text-[#ede8e0] mb-3">Club Details</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            ['Owner',         club.ownerName],
            ['Owner Email',   club.ownerEmail],
            ['Owner Phone',   club.ownerPhone || '—'],
            ['City',          club.address?.city || '—'],
            ['Country',       club.address?.country || '—'],
            ['Max Members',   club.maxMembers],
            ['Created',       new Date(club.createdAt).toLocaleDateString()],
          ].map(([k, v]) => (
            <div key={k}>
              <span style={{ color: '#6b6560' }}>{k}: </span>
              <span style={{ color: '#c5bfb5' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Club Admins — read-only roster for recovery visibility (no actions) */}
      <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#ede8e0]">Club Admins</h3>
          <span className="text-xs" style={{ color: '#6b6560' }}>{clubAdmins.length} admin{clubAdmins.length !== 1 ? 's' : ''}</span>
        </div>
        {clubAdmins.length === 0 ? (
          <p className="text-sm" style={{ color: '#6b6560' }}>No admins found for this club.</p>
        ) : (
          <div className="space-y-2">
            {clubAdmins.map(a => (
              <div key={a._id} className="rounded-lg p-3 border flex items-start justify-between gap-3 flex-wrap"
                style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#ede8e0] truncate">{a.name}</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={a.isPrimary
                        ? { background: 'rgba(251,191,36,0.15)', color: '#fcd34d' }
                        : { background: 'rgba(107,101,96,0.2)', color: '#8a847c' }}>
                      {a.isPrimary ? 'Primary' : 'Staff'}
                    </span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={a.isActive
                        ? { background: 'rgba(34,197,94,0.15)', color: '#4ade80' }
                        : { background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>
                      {a.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-xs mt-1 truncate" style={{ color: '#8a847c' }}>{a.email}</div>
                  <div className="text-xs mt-0.5 flex items-center gap-3 flex-wrap" style={{ color: '#6b6560' }}>
                    {a.phone && <span>{a.phone}</span>}
                    {a.txId && <span className="font-mono">{a.txId}</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 flex-wrap">
                  {!a.isPrimary && (
                    <button
                      onClick={() => setTransferTarget(a)}
                      disabled={setPrimaryMutation.isPending}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                      style={{ background: 'rgba(251,191,36,0.15)', color: '#fcd34d', border: '1px solid rgba(251,191,36,0.3)' }}
                    >
                      Transfer Primary
                    </button>
                  )}
                  {!a.isActive && (
                    <button
                      onClick={() => setConfirmState({
                        title: 'Reactivate admin?',
                        message: `Reactivate ${a.name}? This restores their admin access to the club.`,
                        confirmLabel: 'Reactivate',
                        tone: 'success',
                        onConfirm: () => { reactivateAdminMutation.mutate(a._id); setConfirmState(null); },
                      })}
                      disabled={reactivateAdminMutation.isPending}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                      style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}
                    >
                      Reactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subscription — status-resolved source of truth (same as Club Admin sees) */}
      <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <h3 className="font-semibold text-[#ede8e0] mb-3">Subscription</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span style={{ color: '#6b6560' }}>Plan: </span>
            <span className="capitalize" style={{ color: '#c5bfb5' }}>{sub.planName || club.subscriptionPlan}</span>
          </div>
          <div className="flex items-center gap-2">
            <span style={{ color: '#6b6560' }}>Status: </span>
            {(() => {
              const label = sub.statusDisplay || sub.statusLabel || club.status;
              const ss = SUB_STATUS_STYLE[label] || { bg: 'rgba(107,101,96,0.2)', color: '#8a847c' };
              return (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: ss.bg, color: ss.color }}>
                  {label}
                </span>
              );
            })()}
          </div>

          {/* Trial Ends — only when the club is actually on trial */}
          {sub.isTrial && (
            <div>
              <span style={{ color: '#6b6560' }}>Trial Ends: </span>
              <span style={{ color: '#c5bfb5' }}>{formatAdBs(sub.trialEndsAt)}</span>
            </div>
          )}

          {/* Subscription Ends — only for paid (non-trial) plans with a date */}
          {!sub.isTrial && sub.subscriptionEndsAt && (
            <div>
              <span style={{ color: '#6b6560' }}>Subscription Ends: </span>
              <span style={{ color: '#c5bfb5' }}>{formatAdBs(sub.subscriptionEndsAt)}</span>
            </div>
          )}

          {sub.status === 'suspended' && sub.daysRemaining != null ? (
            // Read daysRemaining directly here, the same way the club-facing Subscription page
            // does (subscriptionStatus.js's daysAgo) — a suspended club's raw daysRemaining is
            // negative (e.g. -41), which must never render as "-41 days". isOverdue is false for
            // a suspended club (by design — it gates the Grace Period block below, which must
            // stay hidden for a club already suspended), so it's never widened here.
            <div>
              <span style={{ color: '#6b6560' }}>Days Remaining: </span>
              <span style={{ color: '#f87171' }}>
                Suspended {Math.abs(sub.daysRemaining)} day{Math.abs(sub.daysRemaining) === 1 ? '' : 's'} ago
              </span>
            </div>
          ) : sub.isOverdue ? (
            <div>
              <span style={{ color: '#6b6560' }}>Days Remaining: </span>
              <span style={{ color: '#f87171' }}>
                Overdue by {sub.overdueDays} day{sub.overdueDays === 1 ? '' : 's'}
              </span>
            </div>
          ) : sub.daysRemaining != null && (
            <div>
              <span style={{ color: '#6b6560' }}>Days Remaining: </span>
              <span style={{ color: sub.daysRemaining <= 3 ? '#f87171' : '#c5bfb5' }}>
                {sub.daysRemaining} day{sub.daysRemaining === 1 ? '' : 's'}
              </span>
            </div>
          )}

          {!sub.isTrial && sub.nextRenewalDate && (
            <div>
              <span style={{ color: '#6b6560' }}>Next Payment Due: </span>
              <span style={{ color: '#c5bfb5' }}>{formatAdBs(sub.nextRenewalDate)}</span>
            </div>
          )}

          {/* Grace period remaining — only while past due (overdue but not yet suspended) */}
          {sub.isOverdue && (
            <div>
              <span style={{ color: '#6b6560' }}>Grace Period Remaining: </span>
              <span style={{ color: '#f87171' }}>
                {sub.graceDaysRemaining > 0
                  ? `${sub.graceDaysRemaining} day${sub.graceDaysRemaining === 1 ? '' : 's'}`
                  : 'Suspension imminent'}
              </span>
            </div>
          )}
        </div>
        {sub.displayMessage && (
          <p className="text-xs mt-3" style={{ color: '#8a847c' }}>{sub.displayMessage}</p>
        )}

        {/* Pending platform payment shortcut → Subscriptions verification queue */}
        {pendingPay && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3"
            style={{ background: '#78350f22', border: '1px solid #a16207' }}>
            <div>
              <p className="text-sm font-semibold" style={{ color: '#fcd34d' }}>Pending payment submitted</p>
              <p className="text-xs mt-0.5" style={{ color: '#8a847c' }}>
                NPR {Number(pendingPay.amount).toLocaleString()} · <span className="capitalize">{pendingPay.method}</span>
              </p>
            </div>
            <Link
              to="/superadmin/subscriptions#pending-payments"
              className="text-xs font-semibold px-3 py-2 rounded-lg"
              style={{ background: '#c8102e', color: '#fff' }}
            >
              Review Payment
            </Link>
          </div>
        )}

      </div>

      {/* Payment Ledger — this club's subscription payments (newest first) */}
      {clubPayments.length === 0 ? (
        <div className="rounded-xl px-5 py-3 border flex items-center justify-between gap-3" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <span className="text-sm font-medium text-[#ede8e0]">Payment History</span>
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: '#6b6560' }}>No payments yet</span>
            {pendingPay && (
              <Link to="/superadmin/subscriptions#pending-payments" className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: '#c8102e', color: '#fff' }}>
                Review
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold text-[#ede8e0]">Payment History</h3>
            {pendingPay && (
              <Link
                to="/superadmin/subscriptions#pending-payments"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background: '#c8102e', color: '#fff' }}
              >
                Review in Subscriptions
              </Link>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                  {['Status', 'Amount', 'Method', 'Reference', 'Submitted', 'Reviewed', 'Reviewed By', 'Proof'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b6560' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clubPayments.map(p => {
                  const pill = PAYMENT_PILL[p.status] || PAYMENT_PILL.pending;
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                        {p.status === 'rejected' && p.rejectionReason && (
                          <div className="text-xs mt-1" style={{ color: '#fca5a5' }}>{p.rejectionReason}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-[#ede8e0] font-medium">NPR {Number(p.amount).toLocaleString()}</td>
                      <td className="px-3 py-2.5 capitalize" style={{ color: '#c5bfb5' }}>{p.method}</td>
                      <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.referenceId || '—'}</td>
                      <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{fmtShort(p.submittedAt)}</td>
                      <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.verifiedAt ? fmtShort(p.verifiedAt) : '—'}</td>
                      <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.reviewedByName || '—'}</td>
                      <td className="px-3 py-2.5">
                        {p.proofUrl ? (
                          <button onClick={() => viewProof(p)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: '#28261f', color: '#c5bfb5' }}>
                            View Proof
                          </button>
                        ) : <span className="text-xs" style={{ color: '#6b6560' }}>None</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active discount */}
      {activeDiscount && (
        <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <h3 className="font-semibold text-[#ede8e0] mb-3 flex items-center gap-2">
            <FaTag className="text-purple-400" /> Active Discount
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['Discount',        activeDiscount.discountId?.name || '—'],
              ['Original Price',  `NPR ${Number(activeDiscount.originalMonthlyPrice).toLocaleString()}/mo`],
              ['Discount Amount', `-NPR ${Number(activeDiscount.discountAmount).toLocaleString()}`],
              ['Final Price',     `NPR ${Number(activeDiscount.finalMonthlyPrice).toLocaleString()}/mo`],
              ['Duration',        activeDiscount.duration + (activeDiscount.durationMonths ? ` (${activeDiscount.durationMonths} mo)` : '')],
              ['Applied',         new Date(activeDiscount.appliedAt).toLocaleDateString()],
            ].map(([k, v]) => (
              <div key={k}>
                <span style={{ color: '#6b6560' }}>{k}: </span>
                <span style={{ color: k === 'Final Price' ? '#4ade80' : k === 'Discount Amount' ? '#34d399' : '#c5bfb5' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent platform access */}
      {!recentAccess?.length ? (
        <div className="rounded-xl px-5 py-3 border flex items-center justify-between gap-3" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <span className="text-sm font-medium text-[#ede8e0]">Recent Platform Access</span>
          <span className="text-sm" style={{ color: '#6b6560' }}>No access recorded</span>
        </div>
      ) : (
        <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <h3 className="font-semibold text-[#ede8e0] mb-3">Recent Platform Access</h3>
          <div className="space-y-2">
            {recentAccess.map((a, i) => (
              <div key={i} className="flex justify-between items-center text-sm py-2 border-b last:border-b-0" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                <div>
                  <span style={{ color: '#c5bfb5' }}>{a.superAdminName}</span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: a.mode === 'full_access' ? '#7f1d1d' : '#28261f', color: a.mode === 'full_access' ? '#fca5a5' : '#8a847c' }}>
                    {a.mode === 'full_access' ? 'Full Access' : 'Read-Only'}
                  </span>
                </div>
                <div className="text-right">
                  <div style={{ color: '#6b6560' }}>{new Date(a.startedAt).toLocaleDateString()}</div>
                  <div className="text-xs" style={{ color: '#6b6560' }}>{a.reason?.slice(0, 40)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Internal Notes (superadmin-only) */}
      <div className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <h3 className="font-semibold text-[#ede8e0] mb-4 flex items-center gap-2">
          <FaStickyNote className="text-yellow-400" /> Internal Notes
          <span className="text-xs font-normal px-2 py-0.5 rounded" style={{ background: '#7f1d1d', color: '#fca5a5' }}>
            Superadmin only
          </span>
        </h3>

        {/* Add note form */}
        <div className="rounded-xl p-4 mb-4" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={3}
            placeholder="Add an internal note about this club (support, billing, deals, technical issues)..."
            className="w-full bg-transparent text-sm outline-none resize-none placeholder-[#6b6560]"
            style={{ color: '#ede8e0' }}
          />
          <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
            <select
              value={noteType}
              onChange={e => setNoteType(e.target.value)}
              style={{ ...inputStyle, width: 'auto', fontSize: 12, padding: '5px 10px' }}>
              <option value="support">Support</option>
              <option value="billing">Billing</option>
              <option value="discount">Discount</option>
              <option value="sales">Sales</option>
              <option value="technical">Technical</option>
              <option value="other">Other</option>
            </select>
            <button
              onClick={() => {
                if (!noteText.trim()) return toast.error('Note cannot be empty');
                addNoteMutation.mutate({ note: noteText.trim(), type: noteType });
              }}
              disabled={addNoteMutation.isPending || !noteText.trim()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-[#ede8e0] transition-colors disabled:opacity-50"
              style={{ background: '#c8102e' }}>
              <FaPlus className="text-xs" />
              {addNoteMutation.isPending ? 'Adding…' : 'Add Note'}
            </button>
          </div>
        </div>

        {/* Notes list */}
        {notes.length === 0 ? (
          <p className="text-sm" style={{ color: '#6b6560' }}>No internal notes yet.</p>
        ) : (
          <div className="space-y-3">
            {notes.map(n => {
              const tc = NOTE_TYPE_COLORS[n.type] || NOTE_TYPE_COLORS.other;
              return (
                <div key={n._id} className="rounded-xl p-4" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: tc.bg, color: tc.color }}>
                          {n.type}
                        </span>
                        <span className="text-xs" style={{ color: '#6b6560' }}>
                          {n.createdByName || 'Admin'} · {new Date(n.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#c5bfb5' }}>{n.note}</p>
                    </div>
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this note? This cannot be undone.')) {
                          deleteNoteMutation.mutate(n._id);
                        }
                      }}
                      disabled={deleteNoteMutation.isPending}
                      className="flex-shrink-0 p-1.5 rounded-lg transition-colors"
                      style={{ color: '#6b6560' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#6b6560')}>
                      <FaTrash className="text-xs" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Danger Zone ── */}
      {club.status !== 'pending_deletion' && !club.purgedAt && (
        <div className="rounded-2xl p-5" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)' }}>
          <p className="font-bold flex items-center gap-2 mb-1" style={{ color: '#f87171' }}>
            <MdWarningAmber /> Danger Zone
          </p>
          <p className="text-xs mb-4" style={{ color: '#8a847c' }}>These actions affect the club's access and data.</p>
          <div className="flex flex-wrap gap-2">
            {club.status !== 'suspended' ? (
              <button onClick={() => setShowSuspend(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border"
                style={{ background: 'transparent', borderColor: 'rgba(255,69,58,0.2)', color: '#ff453a' }}>
                <MdBlock /> Suspend
              </button>
            ) : (
              <button onClick={() => reactivateMutation.mutate()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border"
                style={{ background: 'transparent', borderColor: 'rgba(237,232,224,0.10)', color: '#34d399' }}>
                <MdCheckCircle /> Reactivate
              </button>
            )}
            <button onClick={() => setShowDelete(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
              style={{ background: '#c8102e', color: '#fff' }}>
              <MdDeleteForever /> Delete Club…
            </button>
          </div>
        </div>
      )}

      {showDelete && (
        <DeleteClubModal
          club={club}
          onClose={() => setShowDelete(false)}
          onScheduled={() => qc.invalidateQueries({ queryKey: ['sa-club', id] })}
        />
      )}
      {showPurge && (
        <PurgeModal
          club={club}
          onClose={() => setShowPurge(false)}
          onPurged={() => { qc.invalidateQueries({ queryKey: ['sa-club', id] }); qc.invalidateQueries({ queryKey: ['sa-clubs'] }); }}
        />
      )}

      {showImp  && <ImpersonateModal club={club} onClose={() => setShowImp(false)} />}
      {showSuspend && (
        <SuspendModal
          club={club}
          pending={suspendMutation.isPending}
          onClose={() => setShowSuspend(false)}
          onConfirm={(reason) => suspendMutation.mutate(reason, { onSuccess: () => setShowSuspend(false) })}
        />
      )}
      {confirmState && (
        <ConfirmModal
          {...confirmState}
          onCancel={() => setConfirmState(null)}
        />
      )}
      {showEdit && (
        <EditSubscriptionModal
          club={club}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); qc.invalidateQueries({ queryKey: ['sa-club', id] }); }}
        />
      )}

      {/* Transfer Primary Admin confirmation */}
      {transferTarget && (() => {
        const currentPrimary = clubAdmins.find(a => a.isPrimary);
        const currentPrimaryName = currentPrimary?.name || 'the current Primary Admin';
        return (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => !setPrimaryMutation.isPending && setTransferTarget(null)}>
            <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(251,191,36,0.15)' }}>
                  <MdShield className="text-lg" style={{ color: '#fcd34d' }} />
                </div>
                <h2 className="font-bold text-[#ede8e0]">Transfer Primary Admin?</h2>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#c5bfb5' }}>
                This will make <span className="font-semibold text-[#ede8e0]">{transferTarget.name}</span> the Primary Admin
                for this club. The current Primary Admin, <span className="font-semibold text-[#ede8e0]">{currentPrimaryName}</span>,
                will become Staff Admin. Only one Primary Admin can exist per club.
                {!transferTarget.isActive && ' This admin will also be reactivated.'}
              </p>
              <div className="flex justify-end gap-3 pt-1">
                <button
                  onClick={() => setTransferTarget(null)}
                  disabled={setPrimaryMutation.isPending}
                  className="text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
                  style={{ background: '#28261f', color: '#c5bfb5' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setPrimaryMutation.mutate(transferTarget._id)}
                  disabled={setPrimaryMutation.isPending}
                  className="text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
                  style={{ background: '#d97706', color: '#fff' }}
                >
                  {setPrimaryMutation.isPending ? 'Transferring…' : 'Transfer Primary'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Proof image preview */}
      {proofView && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setProofView(null)}>
          <div className="relative max-w-2xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button onClick={() => setProofView(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-[#ede8e0]"
              style={{ background: '#c8102e' }} aria-label="Close">✕</button>
            <img src={proofView.url} alt="Payment proof" className="max-w-full max-h-[90vh] rounded-xl" />
            <div className="text-center mt-2">
              <a href={proofView.url} target="_blank" rel="noreferrer" className="text-xs" style={{ color: '#c8102e' }}>
                Open original in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
